import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Spin } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { aiChatApi, ChatMessage } from '~/services/ai-chat';

export const AskOzScreen = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsStreaming(true);

    const assistantMessage: ChatMessage = { role: 'assistant', content: '' };
    setMessages([...updatedMessages, assistantMessage]);

    try {
      let accumulated = '';
      for await (const text of aiChatApi.streamChat(updatedMessages)) {
        accumulated += text;
        setMessages([
          ...updatedMessages,
          { role: 'assistant', content: accumulated },
        ]);
      }
    } catch {
      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header */}
      <div className="px-6 py-4 border-b-2 border-black">
        <h1 className="text-xl font-bold">Ask Oz</h1>
        <p className="text-xs text-gray-500 mt-1">
          AI assistant for estimates, packing guidance, and AHC workflows
        </p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <div className="text-4xl mb-4 font-bold">Oz</div>
            <p className="text-sm">Ask me about pricing, packing, estimates, or anything AHC.</p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-lg w-full">
              {[
                'Estimate packing for a 48x60" oil painting',
                'What materials do I need for a sculpture crate?',
                'Suggest team size for a 50-piece gallery install',
                'How should I price a blanket-wrap local delivery?',
              ].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => setInput(suggestion)}
                  className="text-left text-xs px-3 py-2 border border-gray-300 hover:border-black hover:bg-gray-50 transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[75%] px-4 py-3 text-sm whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-black border border-gray-200'
              }`}
            >
              {msg.role === 'assistant' && (
                <div className="text-[10px] font-bold text-gray-400 mb-1 uppercase tracking-wider">
                  Oz
                </div>
              )}
              {msg.content || (isStreaming && i === messages.length - 1 ? (
                <Spin size="small" />
              ) : null)}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="px-6 py-4 border-t-2 border-black">
        <div className="flex gap-2 items-end">
          <Input.TextArea
            ref={inputRef as any}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Oz a question..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={isStreaming}
            className="flex-1 !border-black !rounded-none"
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={isStreaming}
            disabled={!input.trim()}
            className="!bg-black !border-black !rounded-none h-[38px]"
          />
        </div>
        <p className="text-[10px] text-gray-400 mt-2">
          Estimates are approximate. Always review before sending to clients.
        </p>
      </div>
    </div>
  );
};
